import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState, useCallback } from "react";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import CommonTable from "../../common_components/CommonTable";
import TableToolbar from "../../common_components/TableToolbar";
import { ExportButton } from "../../common_components/FormInputs";
import HoverTooltip from "../../common_components/hoverOverlay/HoverTooltip";
import { StyledCurrency } from "../../helper/StyledCurrency";
import { ApiCall } from "../../helper/axios";
import { selectToken } from "../../redux/authSlice";
import useToast from "../../common_components/Toast/useToast";
const DownloadIcon = () => (_jsx("svg", { width: "18", height: "18", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M17.5 12.5V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V12.5M5.83333 8.33333L10 12.5M10 12.5L14.1667 8.33333M10 12.5V2.5", stroke: "#667085", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" }) }));
// Only the columns requested for the Segment Report.
const COLUMN_IDS = [
    // "orders",
    "customers",
    "percentOfTotal",
    "aov",
    "orderValue",
    "averageLifespan",
    "customerValue",
    "purchaseCycle",
    "actions",
];
const SegmentReport = ({ rows, clientId, clientName, endDate, canExportReport, downloadPageLoader, setdownloadPageLoader, setExportBackgroundMessage }) => {
    const token = useSelector(selectToken);
    const toast = useToast();
    /* ── table feature state (same set active-clients uses) ── */
    const [downloadingId, setDownloadingId] = useState(null);
    const [sortConfig, setSortConfig] = useState({ columnId: null, direction: "asc" });
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    const [pinnedColumns, setPinnedColumns] = useState({ left: ['orders', 'customers'], right: ['actions'] });
    const [showFilterRow, setShowFilterRow] = useState(false);
    const [activeFilterColumnId, setActiveFilterColumnId] = useState(null);
    const [columnFilters, setColumnFilters] = useState({});
    const [columnOrder, setColumnOrder] = useState(null);
    const tableRows = useMemo(() => rows.map((r) => ({ ...r, id: r.id })), [rows]);
    const parseSortValue = (value) => {
        if (value == null)
            return "";
        const stringValue = String(value).trim();
        if (stringValue === "" || stringValue === "—")
            return "";
        const numericValue = Number(stringValue.replace(/[^0-9.\-]+/g, ""));
        return Number.isNaN(numericValue) ? stringValue.toLowerCase() : numericValue;
    };
    const sortedTableRows = useMemo(() => {
        if (!sortConfig?.columnId)
            return tableRows;
        return [...tableRows].sort((a, b) => {
            const aValue = parseSortValue(a[sortConfig.columnId]);
            const bValue = parseSortValue(b[sortConfig.columnId]);
            if (typeof aValue === "number" && typeof bValue === "number") {
                return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
            }
            const aText = String(aValue);
            const bText = String(bValue);
            return sortConfig.direction === "asc"
                ? aText.localeCompare(bText, undefined, { numeric: true })
                : bText.localeCompare(aText, undefined, { numeric: true });
        });
    }, [sortConfig, tableRows]);
    // Shared CSV export. `orderSegment` of 0 tells the backend to export all segments.
    // `downloadKey` drives the per-control loading/disabled state ("all" for Export All,
    // the row id for a single segment).
    const runExport = useCallback(async (orderSegment, downloadKey) => {
        if (downloadingId)
            return;
        if (!clientId) {
            toast.error("Select a client before exporting.");
            return;
        }
        if (!endDate) {
            toast.error("Select a month before exporting.");
            return;
        }
        setDownloadingId(downloadKey);
        setdownloadPageLoader(true);
        setExportBackgroundMessage?.(null);
        // True when the export was handed off to the background — the loader +
        // message auto-close on a timer, so the finally block must NOT close
        // the overlay early.
        let handedOffToBackground = false;
        try {
            const response = await ApiCall("POST", "/api/customer-acquisition/export", {
                clientId: clientId,
                order_segment: orderSegment,
                end_date: endDate,
            }, { Authorization: token || "" }, 7 * 60 * 1000, "blob");
            // Large exports don't stream a CSV back — the backend returns a
            // 202 with a small JSON body saying it's being generated in the
            // background and emailed. With responseType "blob" that JSON
            // arrives as a Blob, so read and parse it here.
            if (response?.status === 202) {
                let email = "";
                try {
                    const text = await response.data.text();
                    email = JSON.parse(text)?.data?.email || "";
                }
                catch {
                    // Ignore parse issues — fall back to the generic message.
                }
                handedOffToBackground = true;
                // Show the message below the loader, then auto-close after a few seconds.
                setExportBackgroundMessage?.(email
                    ? `We are processing your export. Once it's ready, we'll send it to ${email}.`
                    : "We are processing your export. Once it's ready, we'll send it to your registered email.");
                setTimeout(() => {
                    setdownloadPageLoader(false);
                    setExportBackgroundMessage?.(null);
                    setDownloadingId(null);
                }, 5000);
                return;
            }
            if (!response || response.status !== 200 || !response.data) {
                throw new Error("Export failed");
            }
            const blob = response.data;
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const segmentPart = orderSegment === 0 ? "all" : `${orderSegment}`;
            const filePrefix = clientName ? `${clientName}_` : "";
            a.download = `${filePrefix}${segmentPart}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            setdownloadPageLoader(false);
        }
        catch (err) {
            console.error("Segment export error:", err);
            setdownloadPageLoader(false);
            toast.error("Failed to download CSV. Please try again.");
        }
        finally {
            // For a background hand-off the overlay is closed by the timer
            // above, so leave it visible with its message here.
            if (!handedOffToBackground) {
                setdownloadPageLoader(false);
                setDownloadingId(null);
            }
        }
    }, [clientId, clientName, endDate, token, downloadingId, toast, setdownloadPageLoader, setExportBackgroundMessage]);
    const handleDownloadRow = useCallback((row) => runExport(row.orderSegment, row.id), [runExport]);
    // "Export All" → order_segment 0 (backend treats 0 as all segments).
    const handleExportAll = useCallback(() => runExport(0, "all"), [runExport]);
    const handleColumnFilterChange = useCallback((columnId, filterValue) => {
        setColumnFilters((prev) => {
            const next = { ...prev };
            if (filterValue)
                next[columnId] = filterValue;
            else
                delete next[columnId];
            return next;
        });
    }, []);
    const handleFilterRowToggle = useCallback((payload = null) => {
        if (!payload || payload?.mode === "all") {
            setActiveFilterColumnId(null);
            setShowFilterRow((prev) => (payload?.open !== undefined ? payload.open : !prev));
            return;
        }
        if (payload?.mode === "single" && payload?.columnId) {
            if (payload?.open === false) {
                setActiveFilterColumnId(null);
                setShowFilterRow(false);
                return;
            }
            setActiveFilterColumnId(payload.columnId);
            setShowFilterRow(true);
        }
    }, []);
    /* ── columns (exactly the requested set) ── */
    const columns = useMemo(() => {
        const cols = [
            { id: "orders", label: "Orders", align: "left", sortable: true },
            { id: "customers", label: "Customers", align: "left", sortable: true },
            { id: "percentOfTotal", label: "% of Total", align: "left", sortable: true },
            { id: "aov", label: "AOV", align: "left", sortable: true },
            { id: "orderValue", label: "Order Value", align: "left", sortable: true },
            { id: "averageLifespan", label: "Average Lifespan", align: "left", sortable: true },
            { id: "customerValue", label: "Customer Value", align: "left", sortable: true },
            { id: "purchaseCycle", label: "Purchase Cycle", align: "left", sortable: true },
        ];
        // Only show actions column if user has export permission
        if (canExportReport) {
            cols.push({ id: "actions", label: "Action", align: "center", sortable: false, hideable: false });
        }
        return cols;
    }, [canExportReport]);
    const renderCell = useCallback((row, column) => {
        if (column.id === "actions") {
            const isDownloading = downloadingId === row.id;
            const disabled = downloadingId !== null;
            return (_jsx(Box, { sx: { display: "flex", justifyContent: "center" }, children: _jsx(HoverTooltip, { title: "Download segment", children: _jsx(Box, { component: "span", onClick: () => !disabled && handleDownloadRow(row), sx: {
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 32,
                            height: 32,
                            borderRadius: "8px",
                            cursor: disabled ? "not-allowed" : "pointer",
                            opacity: disabled && !isDownloading ? 0.4 : 1,
                            "&:hover": { bgcolor: disabled ? "transparent" : "#F9FAFB" },
                        }, children: isDownloading ? _jsx(CircularProgress, { color: "primary", size: 18 }) : _jsx(DownloadIcon, {}) }) }) }));
        }
        if (column.id === "percentOfTotal" || column.id === "aov" || column.id === "orderValue" || column.id === "customerValue") {
            return (_jsx(StyledCurrency, { value: String(row[column.id]), fontSize: "14px", valueColor: "#27272a", symbolColor: "#98a2b3", style: { whiteSpace: "nowrap" } }));
        }
        return (_jsx(Typography, { className: "user-table-email-text", component: "span", sx: { whiteSpace: "nowrap" }, children: row[column.id] }));
    }, [handleDownloadRow, downloadingId]);
    return (_jsxs(Paper, { className: "user-list-card", elevation: 0, children: [_jsxs(Box, { className: "user-list-header-content", sx: {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    backgroundColor: "white",
                    padding: "10px",
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                    border: "2px solid #E5E7EB",
                    borderBottom: "none",
                    gap: "8px",
                }, children: [_jsx(Box, { className: "user-list-toolbar-actions", children: _jsx(TableToolbar, { title: "", showSearch: false, showRows: false, showExpand: false, showFullScreen: true, enableColumnVisibility: true, onColumnVisibilityClick: (event) => setColumnVisibilityAnchor(event.currentTarget), enableColumnFilters: false, showFilterRow: showFilterRow, onFilterRowToggle: () => handleFilterRowToggle({ mode: "all" }), customActions: null }) }), tableRows.length > 0 && canExportReport && (_jsx(ExportButton, { label: "Export All", onClick: handleExportAll, 
                        // loading={downloadingId === "all"}
                        disabled: downloadingId !== null && downloadingId !== "all" }))] }), _jsx(Box, { className: "user-list-table-wrapper", children: _jsx(CommonTable, { columns: columns, rows: sortedTableRows, renderCell: renderCell, expandTrigger: "none", type: "userlist-table segment-report-table", isFirstLoad: false, sortConfig: sortConfig, onSortChange: (columnId, direction) => {
                        setSortConfig(columnId && direction
                            ? { columnId, direction }
                            : { columnId: null, direction: "asc" });
                    }, columnVisibilityConfig: {
                        enable: true,
                        columns: COLUMN_IDS,
                        enableReorder: true,
                    }, hiddenColumns: hiddenColumns, onColumnVisibilityChange: setHiddenColumns, columnVisibilityAnchorEl: columnVisibilityAnchor, onColumnVisibilityClose: () => setColumnVisibilityAnchor(null), enableColumnActions: true, enableColumnPinning: true, pinnedColumns: pinnedColumns, onColumnPinningChange: setPinnedColumns, enableColumnFilters: false, showFilterRow: showFilterRow, activeFilterColumnId: activeFilterColumnId, onActiveFilterColumnIdChange: setActiveFilterColumnId, columnFilters: columnFilters, onColumnFilterChange: handleColumnFilterChange, onFilterRowToggle: handleFilterRowToggle, enableColumnReorder: true, columnOrder: columnOrder, onColumnOrderChange: setColumnOrder, stickyHeader: true }) }), _jsx(Box, { className: "user-list-pagination-wrapper", sx: { paddingInline: "14px" }, children: _jsxs(Box, { className: "pagination-container", children: [_jsx(Box, { className: "pagination-entries-message", children: _jsx(Typography, { className: "pagination-entries-text", children: `Showing ${tableRows.length ? 1 : 0} to ${tableRows.length} of ${tableRows.length} entries` }) }), _jsxs(Box, { className: "pagination-controls", children: [_jsx(Box, { className: "pagination-button-wrapper", children: _jsxs(Box, { component: "button", className: "pagination-nav-button pagination-nav-button-disabled", disabled: true, children: [_jsx("svg", { width: "16", height: "16", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M15.8332 10.0001H4.1665M4.1665 10.0001L9.99984 15.8334M4.1665 10.0001L9.99984 4.16675", stroke: "#414651", strokeWidth: "1.67", strokeLinecap: "round", strokeLinejoin: "round" }) }), _jsx(Typography, { className: "pagination-nav-text", children: "Previous" })] }) }), _jsx(Box, { className: "pagination-numbers", children: _jsx(Box, { className: "pagination-number-base pagination-number-active", children: _jsx(Box, { className: "pagination-number-content", children: _jsx(Typography, { className: "pagination-number-text pagination-number-text-active", children: "1" }) }) }) }), _jsx(Box, { className: "pagination-button-wrapper", children: _jsxs(Box, { component: "button", className: "pagination-nav-button pagination-nav-button-disabled", disabled: true, children: [_jsx(Typography, { className: "pagination-nav-text", children: "Next" }), _jsx("svg", { width: "16", height: "16", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: _jsx("path", { d: "M4.167 10h11.666m0 0L10 4.167M15.833 10 10 15.833", stroke: "#414651", strokeWidth: "1.67", strokeLinecap: "round", strokeLinejoin: "round" }) })] }) })] })] }) })] }));
};
export default SegmentReport;
